import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Row } from 'antd';
import { Table as TableV5 } from 'antd-v5';
import { DeleteOutlined, EditOutlined, FilterFilled, FilterOutlined } from '@ant-design/icons';

import {
  renderCustomExportDateTimeFilterDropdown,
  renderCustomExportGeneralFilterDropdown,
  renderCustomExportNumberFilterDropdown,
  renderCustomExportTextFilterDropdown,
} from '../Exports/renderCustomExportFilterDropdowns';
import {
  extractTextFromCell,
  parseDateTime,
  rowMatchesExportColCombinedFilter,
  rowMatchesExportNumberFilter,
  rowMatchesExportDateFilter,
  applyExportTableFilters,
} from '../Exports/exports.helpers';
import { collator, sortByObjProperty } from '../../common/helpers';

import { batchWorkReportEntries, getWorkReportEntries, updateWorkReportEntry, getABWorkReportingDisplayFields, getSavedABWorkReportFilters } from '../../abWorkReporting/state/abWorkReporting.actions';
import { getIdMap } from '../../helpers/helpers';
import { getBucketTemplateTableColumns } from '../../forms/formHelpers';
import { combineBucketTemplates, generateExport, getAdditionalColumnMap, getWorkReportEntryData, prepareWorkReportEntriesForExport, validateWorkReportEntries, workReportEntryDisplayFieldOptions } from '../../boards/BoardCardABWorkReporting/BoardCardABWorkReportingHelpers';
import { getColumns } from '../../forms/FormBuilder/FormFields/BucketTablePreview';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import useToggle from '../../common/hooks/useToggle';
import useOnDemandBucketCustomData from '../../common/hooks/useOnDemandBucketCustomData';
import { getBuckets } from '../../buckets/state/buckets.actions';
import ABWorkReportFilterDrawer from './ABWorkReportFilterDrawer';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import BoardCardABWorkReportingSettingsDrawer from '../../boards/BoardCardABWorkReporting/BoardCardABWorkReportingSettingsDrawer';
import AbWorkReportingService from '../../abWorkReporting/state/abWorkReporting.service';
import { getStatuses } from '../../statuses/state/statuses.actions';
import { uploadFiles } from '../../helpers/fileHelpers';
import ABWorkReportEditModal from './ABWorkReportEditModal';
import { exportWorkReportToExcel } from './abWorkReportExport.helpers';
import {
  getWorkReportColumnSum,
  isSummableWorkReportColumn,
  SUBTOTAL_NOT_AVAILABLE,
} from './abWorkReportSummary.helpers';

const convertTableColumnsToArray = (tableColumns) => (
  Object.keys(tableColumns).map((key) => ({
    key,
    name: tableColumns[key],
  }))
);

const columnCheck = (displayFieldSet) => (column) => (
  displayFieldSet.has(column.key)
);

const finishedStatuses = new Set(['Exported', 'Voided', 'Historical', 'Pending']);
const allowEditStatuses = new Set(['Active', 'Rejected', 'Exported']);

export default function ABWorkReport() {
  const dispatch = useDispatch();
  const workReportEntries = useSelector((state) => state.abWorkReporting.entries);
  const settings = useSelector((state) => state.settings.company);
  const bucketTemplates = useSelector((state) => state.buckets.bucketTemplates);
  const buckets = useSelector((state) => state.buckets.buckets);
  const statuses = useSelector((state) => state.statuses.statuses);
  const userDisplayFields = useSelector((state) => state.abWorkReporting.displayFields);
  const savedFilters = useSelector((state) => state.abWorkReporting.savedFilters);

  const bucketTemplateMap = getIdMap(bucketTemplates);
  const bucketMap = useMemo(() => getIdMap(buckets), [buckets]);

  const { toggle: toggleFilterDrawer, isToggled: isFilterDrawerVisible } = useToggle();
  const {
    toggle: toggleABWorkReportingSettingsDrawer,
    isToggled: isABWorkReportingSettingsDrawerVisible,
  } = useToggle();

  const [selectedWorkReportEntryIds, setSelectedWorkReportEntryIds] = useState([]);
  const [filters, setFilters] = useState([]);
  const [prevFilters, setPrevFilters] = useState();
  const [loading, setLoading] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [tableFilters, setTableFilters] = useState({});
  const [filteredTableData, setFilteredTableData] = useState();
  const hasAppliedFavoriteFilter = useRef(false);

  useEffect(() => {
    dispatch(getWorkReportEntries());
    dispatch(getStatuses());
    dispatch(getABWorkReportingDisplayFields());
    dispatch(getSavedABWorkReportFilters());
    dispatch(getBuckets());
  }, []);

  // Default-load the user's favorite filter (if any) the first time it loads.
  useEffect(() => {
    if (hasAppliedFavoriteFilter.current || !savedFilters?.length) return;
    hasAppliedFavoriteFilter.current = true;
    const favorite = savedFilters.find((filter) => filter.isFavorite);
    if (favorite?.settings?.length) setFilters(favorite.settings);
  }, [savedFilters]);

  const {
    settings: {
      abWorkReportingMapping: {
        itemId,
        segmentId,
        bidItemId,
        contractField,
        dateField,
        uomField,
        inspectorField,
      } = {},
    } = {},
  } = settings;

  const {
    workReportEntryDisplayFields = [],
  } = userDisplayFields || {};

  const workEntryDisplayFieldSet = new Set(workReportEntryDisplayFields);

  const itemDisplayFieldOptions = convertTableColumnsToArray(
    getBucketTemplateTableColumns({
      data: bucketTemplateMap[itemId],
      type: 'item',
      attachTypeToKey: true,
    }),
  ).filter(
    columnCheck(workEntryDisplayFieldSet),
  );
  const segmentDisplayFieldOptions = convertTableColumnsToArray(
    getBucketTemplateTableColumns({
      data: bucketTemplateMap[segmentId],
      type: 'segment',
      attachTypeToKey: true,
    }),
  ).filter(
    columnCheck(workEntryDisplayFieldSet),
  );
  const bidItemDisplayFieldOptions = convertTableColumnsToArray(
    getBucketTemplateTableColumns({
      data: bucketTemplateMap[bidItemId],
      type: 'bidItem',
      attachTypeToKey: true,
    }),
  ).filter(
    columnCheck(workEntryDisplayFieldSet),
  );
  const workEntryDisplayFields = workReportEntryDisplayFieldOptions.map((field) => ({
    key: field.value,
    name: field.label,
  })).filter(
    columnCheck(workEntryDisplayFieldSet),
  );

  const relevantBucketIds = useMemo(() => {
    const ids = new Set();
    workReportEntries.forEach((entry) => {
      if (entry.itemId) ids.add(entry.itemId);
      if (entry.segmentId) ids.add(entry.segmentId);
      if (entry.bidItemId) ids.add(entry.bidItemId);
    });
    return Array.from(ids);
  }, [workReportEntries]);

  useOnDemandBucketCustomData(relevantBucketIds);

  const workReportEntryData = useMemo(() => getWorkReportEntryData({
    workReportEntries,
    bucketMap,
    filters,
  }), [workReportEntries, bucketMap, filters]);

  const onTableChange = useCallback((_pagination, newFilters, _sorter, extra) => {
    setTableFilters(newFilters || {});
    setFilteredTableData(extra?.currentDataSource);
    if (extra?.action !== 'filter') return;
    const visibleRows = extra?.currentDataSource ?? [];
    const visibleRowIdSet = new Set(visibleRows.map((entry) => entry.id));
    setSelectedWorkReportEntryIds((prev) => {
      const newSelectedIds = [];
      prev.forEach((id) => {
        if (visibleRowIdSet.has(id)) {
          newSelectedIds.push(id);
        }
      });
      return newSelectedIds;
    })
  }, []);

  const selectedWorkReportEntries = useMemo(() => {
    const selectedIdSet = new Set(selectedWorkReportEntryIds);
    return workReportEntryData.filter((entry) => selectedIdSet.has(entry.id));
  }, [workReportEntryData, selectedWorkReportEntryIds]);

  const itemBucket = bucketTemplateMap[itemId];
  const segmentBucket = bucketTemplateMap[segmentId];
  const bidItemBucket = bucketTemplateMap[bidItemId];

  const combinedBucket = combineBucketTemplates({
    itemBucket,
    segmentBucket,
    bidItemBucket,
  });

  const itemStatuses = statuses[itemId] || {};
  const segmentStatuses = statuses[segmentId] || {};
  const bidItemStatuses = statuses[bidItemId] || {};

  const columns = useMemo(() => {
    const orderColumns = [
      ...workEntryDisplayFields,
      ...itemDisplayFieldOptions.map((option) => ({
        ...option,
        name: `Item: ${option.name}`,
      })),
      ...segmentDisplayFieldOptions.map((option) => ({
        ...option,
        name: `Segment: ${option.name}`,
      })),
      ...bidItemDisplayFieldOptions.map((option) => ({
        ...option,
        name: `Bid Item: ${option.name}`,
      })),
    ];

    const columnMap = getIdMap(orderColumns, 'key');
    const additionalColumnMap = getAdditionalColumnMap({
      ...itemStatuses,
      ...segmentStatuses,
      ...bidItemStatuses,
    }, contractField);
    const orderedColumns = [];
    workReportEntryDisplayFields.forEach((key) => {
      const relevantColumn = columnMap[key];
      if (relevantColumn) {
        orderedColumns.push({
          ...relevantColumn,
          dataIndex: additionalColumnMap[key]?.dataIndex || key,
        });
      }
    });

    const buildColumnFilterOptions = (dataIndex) => {
      const unique = new Map();
      workReportEntryData.forEach((row) => {
        const cellStr = extractTextFromCell(row[dataIndex]);
        const parts = cellStr === '' ? [''] : String(cellStr).split(', ');
        parts.forEach((cell) => {
          if (!unique.has(cell)) {
            unique.set(cell, { value: cell, text: cell === '' ? '(empty)' : cell });
          }
        });
      });
      return Array.from(unique.values()).sort(sortByObjProperty('text'));
    };

    const parseNumericCell = (raw) => {
      if (raw == null || raw === '') return Number.NEGATIVE_INFINITY;
      if (typeof raw === 'number') return Number.isNaN(raw) ? Number.NEGATIVE_INFINITY : raw;
      const parsed = parseFloat(String(raw).replace(/[,$\s%]/g, ''));
      return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
    };

    const buildSorter = (col, dataIndex) => {
      if (col.type === 'dateTime') {
        return (a, b) => {
          const dateA = parseDateTime(a[dataIndex]);
          const dateB = parseDateTime(b[dataIndex]);
          const msA = dateA?.isValid ? dateA.toMillis() : Number.NEGATIVE_INFINITY;
          const msB = dateB?.isValid ? dateB.toMillis() : Number.NEGATIVE_INFINITY;
          return msA - msB;
        };
      }
      if (col.type === 'calculation') {
        return (a, b) => parseNumericCell(a[dataIndex]) - parseNumericCell(b[dataIndex]);
      }
      return (a, b) => {
        const textA = String(extractTextFromCell(a[dataIndex]) ?? '');
        const textB = String(extractTextFromCell(b[dataIndex]) ?? '');
        return collator.compare(textA, textB);
      };
    };

    const attachHeaderFilter = (col) => {
      const { dataIndex } = col;
      if (!dataIndex) return col;

      const baseColumnProps = {
        ...col,
        filteredValue: tableFilters[dataIndex] ?? null,
        sorter: buildSorter(col, dataIndex),
        sortDirections: ['ascend', 'descend'],
      };

      switch (col.type) {
        case 'dateTime': {
          const dateCol = { field: dataIndex, dataIndex };
          return {
            ...baseColumnProps,
            filterDropdown: renderCustomExportDateTimeFilterDropdown(dateCol, col.dateOnly),
            onFilter: (value, record) => (
              rowMatchesExportDateFilter(record, dateCol, dataIndex, value)
            ),
          };
        }
        case 'calculation': {
          return {
            ...baseColumnProps,
            filterDropdown: renderCustomExportNumberFilterDropdown,
            onFilter: (value, record) => {
              if (!value) return false;
              return rowMatchesExportNumberFilter(record[dataIndex], value);
            },
          };
        }
        case 'text':
        case 'attribute': {
          return {
            ...baseColumnProps,
            filterDropdown: renderCustomExportTextFilterDropdown,
            onFilter: (value, record) => (
              rowMatchesExportColCombinedFilter(extractTextFromCell(record[dataIndex]), value)
            ),
          };
        }
        default: {
          return {
            ...baseColumnProps,
            filters: buildColumnFilterOptions(dataIndex),
            filterDropdown: renderCustomExportGeneralFilterDropdown,
            onFilter: (value, record) => (
              rowMatchesExportColCombinedFilter(extractTextFromCell(record[dataIndex]), value)
            ),
          }
        }
      }
    };

    return getColumns({
      columns: orderedColumns,
      isDisplay: true,
      requiredColumns: false,
      preventEdits: true,
      responding: false,
      includeLineNumber: false,
      relevantBucketTemplate: combinedBucket,
      additionalColumnMap,
      shouldDisableHistorical: true,
    }).map(attachHeaderFilter).concat([
      {
        title: '',
        dataIndex: '',
        width: 100,
        render: (_, record) => {
          if (!allowEditStatuses.has(record.status)) return null;
          return (
            <EditOutlined
              onClick={(e) => {
                e.stopPropagation();
                setEditEntry(record);
              }}
            />
          );
        },
      },
      {
        title: '',
        dataIndex: '',
        width: 100,
        render: (_, record) => {
          if (record.status === 'Exported' || record.status === 'Voided' || record.status === 'Pending') return null;
          return (
            <DeleteOutlined
              onClick={(e) => {
                e.stopPropagation();
                CustomConfirmModal({
                  title: 'Are you sure you want to void this entry?',
                  content: 'This will void the entry and it will not be available for export.',
                  onOk: () => {
                    dispatch(updateWorkReportEntry(record.id, { entryStatus: 'Voided' }));
                  },
                });
              }}
            />
          );
        },
      }
    ]);
  }, [
    workEntryDisplayFields,
    itemDisplayFieldOptions,
    segmentDisplayFieldOptions,
    bidItemDisplayFieldOptions,
    combinedBucket,
    itemStatuses,
    segmentStatuses,
    bidItemStatuses,
    workReportEntryDisplayFields,
    contractField,
    workReportEntryData,
    tableFilters,
  ]);

  const visibleRowsForExport = useMemo(() => applyExportTableFilters({
    rows: workReportEntryData,
    tableFilters,
    filteredData: filteredTableData,
  }), [workReportEntryData, tableFilters, filteredTableData]);

  const hasSummaries = useMemo(
    () => columns.some(isSummableWorkReportColumn),
    [columns],
  );

  const summariesByDataIndex = useMemo(() => {
    if (!hasSummaries) return {};
    const map = {};
    columns.forEach((col) => {
      if (!isSummableWorkReportColumn(col)) return;
      map[col.dataIndex] = getWorkReportColumnSum(col, visibleRowsForExport);
    });
    return map;
  }, [columns, visibleRowsForExport, hasSummaries]);

  const renderSummary = useCallback(() => (
    <TableV5.Summary fixed>
      <TableV5.Summary.Row>
        <TableV5.Summary.Cell index={0}>
          <div style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Total</div>
        </TableV5.Summary.Cell>
        {columns.map((col, index) => {
          const summaryValue = summariesByDataIndex[col.dataIndex] ?? '';
          const displayValue = summaryValue === SUBTOTAL_NOT_AVAILABLE ? '' : summaryValue;
          return (
            <TableV5.Summary.Cell
              index={index + 1}
              key={col.dataIndex || `summary-${index}`}
            >
              <div style={{ whiteSpace: 'nowrap', fontWeight: displayValue ? 600 : undefined }}>
                {displayValue}
              </div>
            </TableV5.Summary.Cell>
          );
        })}
      </TableV5.Summary.Row>
    </TableV5.Summary>
  ), [columns, summariesByDataIndex])

  const onGenerateCrewWorksheet = async () => {
    if (!contractField || !dateField || !inspectorField) {
      return CustomConfirmModal({
        title: 'Warning',
        content: 'No contract field or date field or inspector field is configured. Please configure a contract field and date field and inspector field in the settings.',
        okText: 'Settings',
        onOk: () => toggleABWorkReportingSettingsDrawer(),
      });
    }

    const error = validateWorkReportEntries({
      workReportEntries: selectedWorkReportEntries,
      dateField,
      contractField,
    });

    if (error) {
      return CustomConfirmModal({
        title: 'Error',
        content: error,
        hideCancel: true,
        okText: 'Cancel',
      });
    }

    setLoading(true);

    const {
      error: exportError,
      message,
      data,
    } = prepareWorkReportEntriesForExport(selectedWorkReportEntries);

    const {
      data: {
        batchNumber,
        worksheetNumber,
      } = {},
    } = await AbWorkReportingService.getLatestNumbers();

    if (exportError || !batchNumber || !worksheetNumber) {
      setLoading(false);
      return CustomConfirmModal({
        title: 'Error',
        content: message,
        hideCancel: true,
        okText: 'Cancel',
      });
    }

    const { exportText, worksheets } = generateExport({
      data,
      batchNumber,
      worksheetNumber,
      contractField,
      uomField,
      inspectorField,
      statuses,
    });

    // generate text file
    const textFile = new File([exportText], `BatchExport_${batchNumber}.txt`, { type: 'text/plain' });
    textFile.path = 'Batch Exports';

    const { payloadFiles = [] } = await uploadFiles([textFile], true);
    const result = await dispatch(batchWorkReportEntries({
      batchNumber,
      worksheets,
      files: payloadFiles,
    }));

    if (result) {
      setLoading(false);
      setSelectedWorkReportEntryIds([]);
      return CustomConfirmModal({
        title: 'Success',
        content: `Crew worksheet generated successfully. Batch number: ${batchNumber}. Export can be found in the Batch Exports folder`,
        okText: 'Close',
        onOk: () => {},
        hideCancel: true,
      });
    }

    setLoading(false);
  };


  const openGenerateCrewWorksheetModal = () => CustomConfirmModal({
    title: 'Generate Crew Worksheet',
    content: 'Are you sure you want to generate a crew worksheet?',
    okText: 'Generate',
    onOk: onGenerateCrewWorksheet,
  });

  const onExportToExcel = () => {
    exportWorkReportToExcel({
      rows: visibleRowsForExport,
      columns,
    });
  };

  // Only enabled if there is selected rows and at least one is active
  const shouldEnableGenerateCrewWorksheet = (
    selectedWorkReportEntries?.length
    && selectedWorkReportEntries.some((entry) => entry.status === 'Active')
    && !selectedWorkReportEntries.some((entry) => finishedStatuses.has(entry.status))
  );

  const tableScrollY = useMemo(() => window.innerHeight - 400, []);
  const tableScrollX = useMemo(
    () => columns.reduce((sum, col) => sum + (col.width || 200), 0),
    [columns],
  );

  const rowSelection = useMemo(() => ({
    selectedRowKeys: selectedWorkReportEntryIds,
    onChange: setSelectedWorkReportEntryIds,
    getCheckboxProps: (record) => ({
      disabled: finishedStatuses.has(record.status),
      className: 'ontraccr-checkbox',
    }),
  }), [selectedWorkReportEntryIds]);

  const onRow = useCallback((record) => ({
    onClick: () => {
      if (finishedStatuses.has(record.status)) return;
      if (selectedWorkReportEntryIds.includes(record.id)) {
        setSelectedWorkReportEntryIds(
          selectedWorkReportEntryIds.filter((id) => id !== record.id),
        );
      } else {
        setSelectedWorkReportEntryIds([...selectedWorkReportEntryIds, record.id]);
      }
    },
    className: 'timecard-entry-row',
  }), [selectedWorkReportEntryIds]);

  const onHeaderRow = useCallback(() => ({
    className: 'ontraccr-table-header',
  }), []);

  if (prevFilters !== filters) {
    setPrevFilters(filters);
    setSelectedWorkReportEntryIds([]);
  }

  return (
    <>
      <Row style={{ width: '100%', height: 32 }}>
        <OnTraccrButton
          title=""
          onClick={toggleFilterDrawer}
          icon={(
            filters?.length
              ? <FilterFilled style={{ marginLeft: 0 }} />
              : <FilterOutlined style={{ marginLeft: 0 }} />
          )}
        />
        <OnTraccrButton
          title="Export to Excel"
          onClick={onExportToExcel}
          disabled={!visibleRowsForExport?.length}
          style={{ marginLeft: 10 }}
        />
        <OnTraccrButton
          title="Generate Crew Worksheet"
          onClick={openGenerateCrewWorksheetModal}
          disabled={!shouldEnableGenerateCrewWorksheet}
          style={{ marginLeft: 10 }}
          loading={loading}
        />
        <OnTraccrButton
          title="Settings"
          onClick={toggleABWorkReportingSettingsDrawer}
          style={{ marginLeft: 'auto' }}
        />
      </Row>
      <div style={{ width: '100%', height: 'calc(100% - 50px)', background: 'white' }}>
        <TableV5
          virtual
          className="ab-work-report-table"
          dataSource={workReportEntryData}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ x: tableScrollX, y: tableScrollY }}
          rowSelection={rowSelection}
          onRow={onRow}
          onHeaderRow={onHeaderRow}
          onChange={onTableChange}
          summary={hasSummaries ? renderSummary : undefined}
        />
      </div>
      <ABWorkReportFilterDrawer
        visible={isFilterDrawerVisible}
        onClose={toggleFilterDrawer}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
      />
      <BoardCardABWorkReportingSettingsDrawer
        visible={isABWorkReportingSettingsDrawerVisible}
        onClose={toggleABWorkReportingSettingsDrawer}
      />
      <ABWorkReportEditModal
        editEntry={editEntry}
        setEditEntry={setEditEntry}
        bidItemStatuses={bidItemStatuses}
        dateField={dateField}
        statuses={statuses}
        bidItemBucketTemplateId={bidItemId}
        segmentBucketTemplateId={segmentId}
        bucketMap={bucketMap}
      />
    </>
  );
}
